import { Stack } from 'common/ui/Layout';
import Text from 'common/ui/Text';
import { Colors } from 'styles';
import { css } from 'styled-components';
import { StatusIndicator } from 'domains/reporter/Reporter/Toolbars/ReportToolbar/StatusIndicator';
import { ReportSignedByLabel } from './ReportSignedByLabel';
import { FocusModeToggleMethod } from 'hooks/useFocusMode';
import { ReporterSaveStateType } from 'config/recoilState';
import { logger } from 'modules/logger';
import { FocusModeButton } from './FocusModeButton';

type UnifiedEditorTopBarProps = {
  isFocusModeSettingOn: boolean;
  toggleFocusMode: (args: { method: FocusModeToggleMethod; enabled?: boolean }) => void;
  isFocusModeOn: boolean;
  label?: string | null;
  isReportEditor: boolean;
  saveStatus: ReporterSaveStateType;
  lastUpdated?: Date | null;
  reportOwner?: { firstName: string; lastName: string } | null;
  reportSubmittedAt?: Date | null;
  isAddendumEditor: boolean;
  addendumOwner?: { firstName: string; lastName: string };
  addendumSubmittedAt?: Date | null;
  shouldShowFocusModeToggle: boolean;
  shouldShowStatusIndicator: boolean;
  shouldShowTemplateName: boolean;
  templateName: string | undefined;
};

export const UnifiedEditorTopBar = ({
  isFocusModeSettingOn,
  toggleFocusMode,
  isFocusModeOn,
  label,
  isReportEditor,
  saveStatus,
  lastUpdated,
  reportOwner,
  reportSubmittedAt,
  isAddendumEditor,
  addendumOwner,
  addendumSubmittedAt,
  shouldShowFocusModeToggle,
  shouldShowStatusIndicator,
  shouldShowTemplateName,
  templateName,
}: UnifiedEditorTopBarProps) => {
  const handleToggleFocusMode = () => {
    logger.info('Focus mode toggled via form control');
    toggleFocusMode({ method: FocusModeToggleMethod.FormControl });
  };
  const shouldShowFocusModeTemplateDivider =
    isFocusModeSettingOn && shouldShowFocusModeToggle && shouldShowTemplateName;

  return (
    <Stack
      alignX={isFocusModeSettingOn || shouldShowTemplateName || label != null ? 'between' : 'end'}
      alignY="center"
      css={css`
        margin-bottom: 2px !important;
      `}
    >
      {label != null && (
        <Text
          variant="body1"
          color={Colors.gray8}
          css={
            isAddendumEditor
              ? css`
                  font-weight: bold;
                `
              : undefined
          }
        >
          {label}
        </Text>
      )}
      <Stack alignY="center">
        {isFocusModeSettingOn && shouldShowFocusModeToggle && (
          <div data-testid="focus-mode-form-input">
            <FocusModeButton isFocusModeOn={isFocusModeOn} onClick={handleToggleFocusMode} />
          </div>
        )}
        {shouldShowFocusModeTemplateDivider && (
          <span
            aria-hidden
            data-testid="focus-mode-template-divider"
            css={css`
              display: inline-flex;
              align-items: center;
              align-self: center;
              height: 24px;
              padding-left: 12px;
              padding-right: 12px;

              &::before {
                content: '';
                width: 1px;
                height: 12px;
                background-color: ${Colors.gray8};
              }
            `}
          />
        )}
        {shouldShowTemplateName && (
          <Text
            variant="caption"
            css={css`
              margin-left: ${shouldShowFocusModeTemplateDivider ? 0 : 14}px;
              margin-top: ${shouldShowFocusModeTemplateDivider ? 0 : 5}px;
              color: ${Colors.gray8};
            `}
          >
            Template: {templateName == null ? 'Sirona Default' : templateName}
          </Text>
        )}
      </Stack>

      <Stack
        alignX="center"
        alignY="center"
        css={css`
          margin-right: 2px;
          ${isReportEditor ? 'height: 16px;' : ''}
        `}
      >
        {shouldShowStatusIndicator && (
          <div data-testid="status-indicator-wrapper">
            <StatusIndicator status={saveStatus} lastUpdated={lastUpdated} />
          </div>
        )}
        {!shouldShowStatusIndicator && reportSubmittedAt != null && !isAddendumEditor && (
          <ReportSignedByLabel
            firstName={reportOwner?.firstName}
            lastName={reportOwner?.lastName}
            submittedAt={reportSubmittedAt}
          />
        )}
        {isAddendumEditor && addendumSubmittedAt != null && (
          <ReportSignedByLabel
            firstName={addendumOwner?.firstName}
            lastName={addendumOwner?.lastName}
            submittedAt={addendumSubmittedAt}
          />
        )}
      </Stack>
    </Stack>
  );
};
